import {
  commitUpdateIssueIssueTypeMutation,
  type OptimisticIssueType,
} from '@github-ui/item-picker/commitUpdateIssueIssueTypeMutation'
import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import type {IssueTypePickerIssueType} from '@github-ui/item-picker-issue-type'
import {IssueTypePicker} from '@github-ui/item-picker-issue-type-tsq'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useAnalytics} from '@github-ui/use-analytics'
import {useCallback, useMemo} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {LABELS} from '../../constants/labels'
import {ReadonlySectionHeader} from '../ReadonlySectionHeader'
import {Section} from '../Section'
import {SectionHeader} from '../SectionHeader'
import type {IssueViewerIssueNode} from './issue-viewer-view-query-types'
import {IssueType} from './IssueType'
import styles from './TypesSection.module.css'

export type TypesSectionTypeFragment = Pick<IssueViewerIssueNode, 'issueType'>

type TypesSectionRepository = Pick<
  NonNullable<IssueViewerIssueNode['repository']>,
  'name' | 'nameWithOwner' | 'issueTypes'
> & {
  owner: Pick<NonNullable<NonNullable<IssueViewerIssueNode['repository']>['owner']>, 'login'>
}

export type TypesSectionFragment = Pick<IssueViewerIssueNode, 'id' | 'issueType' | 'viewerCanType'> & {
  repository: TypesSectionRepository
}

export const ReadonlyTypesSectionHeader = () => <ReadonlySectionHeader title={LABELS.sectionTitles.types} />

type TypesSectionProps = {
  sectionHeader: React.ReactElement
  type: IssueTypePickerIssueType | null
  repoNameWithOwner?: string
  className?: string
}

export const TypesSection = ({sectionHeader, type, repoNameWithOwner, className}: TypesSectionProps) => (
  <Section
    id={`sidebar-types-section`}
    className={className}
    sectionHeader={sectionHeader}
    emptyText={type ? undefined : LABELS.emptySections.types}
  >
    <div className={styles.typeContainer}>
      <IssueType type={type} repoNameWithOwner={repoNameWithOwner} />
    </div>
  </Section>
)

type EditIssueIssueTypesSectionProps = {
  issue: TypesSectionFragment
  onIssueUpdate?: () => void
  insideSidePanel?: boolean
  readonly: boolean
}
export function EditIssueIssueTypeSection({
  issue,
  onIssueUpdate,
  insideSidePanel,
  readonly,
}: EditIssueIssueTypesSectionProps) {
  const {
    repository: {
      name: repo,
      owner: {login: owner},
      nameWithOwner,
      issueTypes,
    },
    id: issueId,
    viewerCanType,
  } = issue

  const formattedIssueTypes = issueTypes?.edges?.map(edge => edge?.node?.id) || []

  const activeType = issue.issueType || null

  const {sendAnalyticsEvent} = useAnalytics()
  const {addToast} = useToastContext()
  const environment = useRelayEnvironment()

  const onSelectionChanged = useCallback(
    (selectedTypes: IssueTypePickerIssueType[]) => {
      const selectedType = selectedTypes.length > 0 ? selectedTypes[0] : null
      const issueTypeId = selectedType?.id ?? null
      sendAnalyticsEvent('issue_viewer.update_issue_type', 'ISSUE_VIEWER_UPDATE_ISSUE_TYPE_DIALOG', {
        initialIssueTypeId: activeType?.id ?? '',
        issueTypeId: issueTypeId ?? '',
      })

      // Build optimistic issue type from the selected type for instant UI update
      const optimisticIssueType: OptimisticIssueType | null = selectedType
        ? {
            id: selectedType.id,
            name: selectedType.name,
            description: selectedType.description,
            color: selectedType.color,
            isEnabled: selectedType.isEnabled,
            pinnedFields: selectedType.pinnedFields,
          }
        : null

      commitUpdateIssueIssueTypeMutation({
        environment,
        input: {issueId, issueTypeId},
        optimisticIssueType,
        onError: () =>
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: ERRORS.couldNotUpdateType,
          }),
        onCompleted: onIssueUpdate,
      })
    },
    [activeType?.id, addToast, environment, issueId, onIssueUpdate, sendAnalyticsEvent],
  )

  const sectionHeader = useMemo(() => {
    const readonlyType = readonly || !viewerCanType
    if (readonlyType) {
      return <ReadonlyTypesSectionHeader />
    }

    return (
      <IssueTypePicker
        owner={owner}
        repo={repo}
        onSelectionChange={onSelectionChanged}
        anchorElement={(anchorProps, ref) => (
          <SectionHeader title={LABELS.sectionTitles.types} buttonProps={anchorProps} ref={ref} />
        )}
        readonly={readonlyType}
        activeIssueType={activeType ?? null}
        insidePortal={insideSidePanel}
        width="medium"
      />
    )
  }, [readonly, viewerCanType, owner, repo, onSelectionChanged, activeType, insideSidePanel])

  // Hide the entire section when the org has no issue types defined, so we don't
  // render an empty, confusing section for a feature the org has removed.
  if (!formattedIssueTypes.some(Boolean)) return null

  return <TypesSection sectionHeader={sectionHeader} type={activeType ?? null} repoNameWithOwner={nameWithOwner} />
}
